import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useMaps } from './maps'
import { useDrawingStore } from './drawing'
import { useMeasure } from './measuring'
import { 
    postExtractGeometryFromShape,
    deleteOldFieldROI ,
    generateROIHeadlands,
    postSegmentationInteractionPoints
} from '../scripts/fieldsPlanning'
import {v4 as uuid} from 'uuid'
import { useFieldsConnectionStore } from './fieldsConnection'
import { useSegmentsStore } from './segments'
import { geoJsonRingToOpenPath } from '@/helpers/geometryHelper'

export const useRoiStore = defineStore('roi', {
    state: () => ({
        roisType: 'obstaclesROIs',
        geometryType: '',
        geometryElements: [],
        isStartingDrawing: false,
        selectedElementId: null,
        isModified: false,
        localData: {
            type: null,
            message: '',
            height: null,
            validFrom: null,
            validTo: null,
            driveable: false,
            active: true,
            isSmall: true,
            headlands: null,
            headlandsSegments: null,
            segments: []
        },
        obstacleTypeOptions: [
            { label: 'Cutout', value: 'cutout', icon: 'pi pi-exclamation-circle' },
            // { label: 'Obstacle', value: 'obstacle', icon: 'pi pi-exclamation-triangle' },
            { label: 'Entry Point', value: 'entrypoint', icon: 'pi pi-arrow-right' },
            { label: 'Logistic', value: 'logistic', icon: 'pi pi-box' },
        ],
        originalData: null,
        toastMessage: [null, null, null],
        editingRoi: false,
        highlightedRoiId: null,
    }),
    actions: {
        /** Highlights the ROI card in the list and scrolls to it (on map ROI click). */
        highlightRoiInList(roiId) {
            this.highlightedRoiId = roiId
            document.getElementById(`roi-${roiId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        },
        setExistingElementsFromGeojson(features) {
            const newElements = this.geometryElements.filter(el => !el.isOld)
            
            if (!features?.length) {
                this.geometryElements = newElements
                return
            }

            const oldElements = features.map(feature => {
                const geom = feature?.geometry
                if (!geom) {
                    return null
                }

                const { type, path } = this.parseGeometry(geom)
                const props = feature.properties || {}
                const validFrom = props.valid_from ? new Date(props.valid_from) : null
                const validTo = props.valid_to ? new Date(props.valid_to) : null
                return {
                    id: feature.id,
                    type,
                    path,
                    feature,
                    overlay: null,
                    geometryType: geom.type,
                    roisType: props.type || this.roisType,
                    createdAt: validFrom || new Date(),
                    isOld: true,
                    properties: {
                        type: props.type || '',
                        message: props.commit_msg || '',
                        height: props.height ?? null,
                        validFrom,
                        validTo,
                        driveable: props.driveable ?? false,
                        active: props.active ?? true,
                        isSmall: props.is_small ?? false,
                        headlandsSegments: props.headlandsSegments ?? null,
                        head_segments_id: props.head_segments_id ?? null
                    }
                }
            }).filter(Boolean)

            this.geometryElements = [...newElements, ...oldElements]
        },
        parseGeometry(geom) {
            const coords = geom.coordinates
            switch (geom.type) {
                case 'Point':
                    return { type: 'point', path: [{ lat: coords[1], lng: coords[0] }] }
                case 'LineString':
                    return { type: 'line', path: coords.map(([lng, lat]) => ({ lat, lng })) }
                case 'Polygon':
                    return { type: 'polygon', path: geoJsonRingToOpenPath(coords[0] || []) }
                case 'MultiPolygon':
                    return { type: 'polygon', path: geoJsonRingToOpenPath(coords[0]?.[0] || []) }
                case 'MultiLineString':
                    return { type: 'line', path: (coords[0] || []).map(([lng, lat]) => ({ lat, lng })) }
                default:
                    return { type: 'polygon', path: [] }
            }
        },
        async startDrawing() {
            const mapStore = useMaps()
            const ds = useDrawingStore()
            const measureStore = useMeasure()
            
            this.isStartingDrawing = true

            // Leftover measure overlays (Stop without Clear) steal the map visuals
            if (measureStore.measureBtnState) {
                measureStore.clearMeasuring(measureStore.buttonContainer)
            }
            
            if (!mapStore.terraDraw) {
                console.error('TerraDraw not initialized')
                return
            }
            
            // Cleanup own listener
            if (this.terraDrawListener) {
                mapStore.terraDraw.off('finish', this.terraDrawListener)
            }
            
            // Cleanup drawing store listener to avoid conflicts
            ds.cleanupDrawingListener()
            
            mapStore.terraDraw.start()
            mapStore.terraDraw.setMode(this.geometryType)
            this.setupDrawingListener()
        },
        setupDrawingListener() {
            const mapStore = useMaps()
            const ds = useDrawingStore()
            
            this.terraDrawListener = async (id) => {
                const features = mapStore.terraDraw.getSnapshot()
                const feature = features.find(f => f.id === id)
                
                if (feature) {
                    const exists = this.geometryElements.some(el => el.id === feature.id)
                    if (exists) {
                        return
                    }
                    
                    const converted = ds.convertTerraDrawFeatureToGoogleMapsOverlay(feature)
                    
                    if (converted) {
                        this.geometryElements.push({
                            id: feature.id,
                            type: converted.type,
                            overlay: converted.overlay,
                            path: converted.path,
                            feature: feature,
                            geometryType: this.geometryType,
                            roisType: this.roisType,
                            createdAt: new Date(),
                            // ROI Properties
                            properties: this.createDefaultProperties(this.geometryType)
                        })
                        if (converted.type === 'point') {
                            this.setupPointDragListener(feature.id)
                        } else {
                            this.setupGeoListeners(feature.id)
                            converted.overlay.addListener('click', () => this.highlightRoiInList(feature.id))
                        }
                        ds.handleContextMenu(converted.overlay)
                        mapStore.terraDraw.removeFeatures([feature.id])
                        
                        mapStore.terraDraw.setMode('select')
                        this.geometryType = ''
                    } else {
                        console.error('Could not convert feature')
                        mapStore.terraDraw.removeFeatures([feature.id])
                    }
                }
            }
            mapStore.terraDraw.on('finish', this.terraDrawListener)
        },
        async removeObstacleROI(id) {
            const element = this.geometryElements.find(element => element.id === id)
            
            if (element) {
                // Cleanup path listeners for LineString
                if (element.pathListeners) {
                    const mapStore = useMaps()
                    element.pathListeners.forEach(listener => {
                        mapStore.google.maps.event.removeListener(listener)
                    })
                    element.pathListeners = null
                }
                
                // Cleanup drag listener for Point
                if (element.dragListener) {
                    const mapStore = useMaps()
                    mapStore.google.maps.event.removeListener(element.dragListener)
                    element.dragListener = null
                }

                if (element.overlay) {
                    const rawOverlay = toRaw(element.overlay)
                    if (rawOverlay.setMap) {
                        rawOverlay.setMap(null)
                    } else if (rawOverlay.map !== undefined) {
                        rawOverlay.map = null
                    }
                }
                this.geometryElements = this.geometryElements.filter(el => el.id !== id)
                return true
            }
            return false
        },
        updateElementProperties(id, properties) {
            const element = this.geometryElements.find(el => el.id === id)
            if (element) {
                element.properties = {
                    ...element.properties,
                    ...properties
                }
                return true
            }
            return false
        },
        getTypeOptionsForElement(element) {
            const geometryType = element?.geometryType?.toLowerCase?.() || ''
            let filteredObstacleOptions = this.obstacleTypeOptions
            if (geometryType !== 'linestring') {
                filteredObstacleOptions = filteredObstacleOptions.filter(
                    option => option.value !== 'entrypoint' && option.value !== 'logistic'
                )
            }
            // Point and LineString are not allowed to be obstacles
            if (geometryType === 'point' || geometryType === 'linestring' || geometryType === 'line') {
                return filteredObstacleOptions.filter(
                    option => option.value !== 'obstacle' && option.value !== 'cutout'
                )
            }
            return filteredObstacleOptions
        },
        /** When only one type is valid for the geometry, set it on the element (clears "no type" warning). */
        ensureDefaultType(element) {
            if (!element?.properties || element.properties.type) {
                return element?.properties?.type || ''
            }
            const options = this.getTypeOptionsForElement(element)
            if (options.length !== 1) {
                return ''
            }
            element.properties.type = options[0].value
            return element.properties.type
        },
        createDefaultProperties(geometryType) {
            const options = this.getTypeOptionsForElement({ geometryType })
            return {
                type: options.length === 1 ? options[0].value : '',
                message: '',
                height: null,
                validFrom: null,
                validTo: null,
                driveable: false,
                active: true,
                isSmall: true
            }
        },
        setSelectedElementId(id) {
            const seg = useSegmentsStore()
            // Disable editing on previously selected old element
            if (this.selectedElementId && this.selectedElementId !== id) {
                seg.clearAll()
                const previousElement = this.geometryElements.find(el => el.id === this.selectedElementId)
                if (previousElement && previousElement.isOld) {
                    this.setOldElementEditable(previousElement, false)
                }
            }
            this.selectedElementId = id
            
            // Enable editing on selected old element
            if (id) {
                const element = this.geometryElements.find(el => el.id === id)
                if (element && element.isOld) {
                    this.setOldElementEditable(element, true)
                }
            }
        },
        // Find overlay from fieldsConnection and set editable (Old ROIs)
        setOldElementEditable(element, editable) {
            const fcs = useFieldsConnectionStore()
            const ds = useDrawingStore()

            const overlay = fcs.generatedROIs?.find(roi => toRaw(roi).roiId === element.id)
            if (!overlay) {
                console.error('Overlay not found for element:', element.id)
                return
            }

            const raw = toRaw(overlay)
            
            if (editable) {
                // Save original geometry for discard
                element.originalPath = JSON.parse(JSON.stringify(element.path))
                element.originalGeometry = JSON.parse(JSON.stringify(element.feature.geometry))
                element.overlay = overlay
                
                // Make editable
                raw.setEditable?.(true)
                if (raw.gmpDraggable !== undefined) {
                    raw.gmpDraggable = true
                }
                
                // Setup listeners
                element.type === 'point' ? this.setupPointDragListener(element.id) : this.setupGeoListeners(element.id)
                ds.handleContextMenu(raw)
            } else {
                // Make non-editable
                raw.setEditable?.(false)
                if (raw.gmpDraggable !== undefined) {
                    raw.gmpDraggable = false
                }
                
                // Cleanup
                this.cleanupElementListeners(element)
                element.overlay = null
                element.originalPath = null
                element.originalGeometry = null
            }
        },
        cleanupElementListeners(element) {
            const mapStore = useMaps()
            if (element.pathListeners) {
                element.pathListeners.forEach(l => mapStore.google.maps.event.removeListener(l))
                element.pathListeners = null
            }
            if (element.dragListener) {
                mapStore.google.maps.event.removeListener(element.dragListener)
                element.dragListener = null
            }
        },
        // Prepare data for saving NEW ROIs to API
        prepareDataForSave(fieldId) {
            return this.geometryElements
                .filter(element => !element.isOld)
                .map(element => ({
                    field_id: fieldId,
                    parent_id: null,
                    driveable: element.properties.driveable,
                    active: element.properties.active,
                    type: element.properties.type || 'obstacle',
                    is_small: element.properties.isSmall,
                    height: element.properties.height,
                    valid_from: element.properties.validFrom ? element.properties.validFrom.toISOString() : null,
                    valid_to: element.properties.validTo ? element.properties.validTo.toISOString() : null,
                    geometry: element.feature.geometry,
                    generator_version: 1,
                    commit_msg: element.properties.message || 'add new obstacle ROI'
                }))
        },
        // Prepare data for updating OLD modified ROIs
        prepareDataForUpdate(fieldId) {
            return this.geometryElements
                .filter(element => element.isOld && element.isModified)
                .map(element => ({
                    id: element.id,
                    field_id: fieldId,
                    driveable: element.properties.driveable,
                    active: element.properties.active,
                    type: element.properties.type || 'obstacle',
                    is_small: element.properties.isSmall,
                    height: element.properties.height,
                    valid_from: element.properties.validFrom ? element.properties.validFrom.toISOString() : null,
                    valid_to: element.properties.validTo ? element.properties.validTo.toISOString() : null,
                    geometry: element.feature.geometry,
                    generator_version: element.feature.properties?.generator_version || 1,
                    commit_msg: element.properties.message || 'update obstacle ROI'
                }))
        },
        prepareCutoutSegmentsForSave(fieldId) {
            return this.geometryElements
                .filter(element => !element.isOld && element.properties.type === 'cutout' && element.properties.headlandsSegments)
                .map(element => ({
                    field_id: fieldId,
                    valid_from: element.properties.validFrom ? element.properties.validFrom.toISOString() : null,
                    valid_to: element.properties.validTo ? element.properties.validTo.toISOString() : null,
                    geometry: element.properties.headlandsSegments,
                    generator_version: element.feature.properties?.generator_version || 1,
                    commit_msg: element.properties.message || 'add new cutout segment'
                }))
        },
        prepareCutoutSegmentsForUpdate(fieldId) {
            return this.geometryElements
                .filter(element => element.isOld && element.isModified && element.properties.type === 'cutout' && element.properties.headlandsSegments)
                .map(element => ({
                    field_id: fieldId,
                    id: element.properties.head_segments_id || null,
                    valid_from: element.properties.validFrom ? element.properties.validFrom.toISOString() : null,
                    valid_to: element.properties.validTo ? element.properties.validTo.toISOString() : null,
                    parent_cutout_id: element.id,
                    geometry: element.properties.headlandsSegments,
                    generator_version: element.feature.properties?.generator_version || 1,
                    commit_msg: element.properties.message || 'update cutout segment',
                    head_segments_id: element.properties.head_segments_id || null
                }))
        },
        // Clear modified flag after successful save
        clearModifiedFlags() {
            this.geometryElements.forEach(element => {
                if (element.isOld) {
                    element.isModified = false
                }
            })
        },
        clearAllElements() {
            const seg = useSegmentsStore()
            this.geometryElements.forEach(element => {
                // Cleanup path listeners
                if (element.pathListeners) {
                    const mapStore = useMaps()
                    element.pathListeners.forEach(listener => {
                        mapStore.google.maps.event.removeListener(listener)
                    })
                }
                
                // Cleanup drag listener
                if (element.dragListener) {
                    const mapStore = useMaps()
                    mapStore.google.maps.event.removeListener(element.dragListener)
                }

                if (element.overlay) {
                    const rawOverlay = toRaw(element.overlay)
                    if (rawOverlay.setMap) {
                        rawOverlay.setMap(null)
                    } else if (rawOverlay.map !== undefined) {
                        rawOverlay.map = null
                    }
                }
            })
            this.clearSelection()
            this.geometryElements = []
            this.geometryType = null
        },
        clearNewElements() {
            // Cleanup and remove new elements
            this.geometryElements.filter(el => !el.isOld).forEach(el => {
                this.cleanupElementListeners(el)
                toRaw(el.overlay)?.setMap?.(null)
            })
            
            // Keep only old elements
            this.geometryElements = this.geometryElements.filter(el => el.isOld)
            
            // Clear selection if element no longer exists
            if (this.selectedElementId && !this.geometryElements.some(el => el.id === this.selectedElementId)) {
                this.selectedElementId = null
            }
        },
        setupPointDragListener(elementId) {
            const mapStore = useMaps()
            const element = this.geometryElements.find(el => el.id === elementId)
            
            if (!element || element.type !== 'point') return
            
            const rawMarker = toRaw(element.overlay)
            
            // Clear old listener
            if (element.dragListener) {
                mapStore.google.maps.event.removeListener(element.dragListener)
            }
            
            // Add new listener
            element.dragListener = mapStore.google.maps.event.addListener(rawMarker, 'dragend', () => {
                const newPosition = rawMarker.position
                // Update path
                element.path = [{ lat: newPosition.lat, lng: newPosition.lng }]
                // Update feature.geometry.coordinates (GeoJSON format: [lng, lat])
                element.feature.geometry.coordinates = [newPosition.lng, newPosition.lat]
                // Mark as modified for old elements
                if (element.isOld) {
                    element.isModified = true
                }
            })
        },
        setupGeoListeners(elementId) {
            const mapStore = useMaps()
            const element = this.geometryElements.find(el => el.id === elementId)
            
            if (!element || (element.type !== 'line' && element.type !== 'polygon')) return
            
            // Cleanup old listeners
            if (element.pathListeners) {
                element.pathListeners.forEach(listener => {
                    mapStore.google.maps.event.removeListener(listener)
                })
            }
            // Add new listeners
            element.pathListeners = this.addPathEditListeners(elementId)
        },
        addPathEditListeners(elementId) {
            const mapStore = useMaps()
            const element = this.geometryElements.find(el => el.id === elementId)
            
            if (!element || (element.type !== 'line' && element.type !== 'polygon')) return []
            
            const rawOverlay = toRaw(element.overlay)
            const path = rawOverlay.getPath()
            
            // Create update callback
            const updateCoordinates = async () => {
                await this.updateElementCoordinates(elementId)
                this.isModified = true
                // Mark element as modified for old elements
                if (element.isOld) {
                    element.isModified = true
                }
                this.updateSegments(elementId)
            }
            // Add listeners for all edit events
            return [
                // Vertex moved
                mapStore.google.maps.event.addListener(path, 'set_at', updateCoordinates),
                // Vertex added
                mapStore.google.maps.event.addListener(path, 'insert_at', updateCoordinates),
                // Vertex deleted
                mapStore.google.maps.event.addListener(path, 'remove_at', updateCoordinates),
                // Whole geometry moved (drag)
                mapStore.google.maps.event.addListener(rawOverlay, 'dragend', updateCoordinates)
            ]
        },
        /** Editable polygon on map — does not touch geometryElements. */
        createEditablePolygonDraft(feature, styleOptions = {}) {
            const ms = useMaps()
            const ds = useDrawingStore()
            const geometry = feature?.geometry

            if (geometry?.type !== 'Polygon') {
                return null
            }

            const paths = geoJsonRingToOpenPath(geometry.coordinates[0])

            const overlay = new ms.google.maps.Polygon({
                paths: [paths],
                fillOpacity: styleOptions.fillOpacity ?? 0.5,
                strokeWeight: styleOptions.strokeWeight ?? 2,
                strokeColor: styleOptions.strokeColor ?? '#ff0000',
                fillColor: styleOptions.fillColor ?? '#ff0000',
                clickable: true,
                editable: true,
                zIndex: styleOptions.zIndex ?? 1000,
            })
            overlay.setMap(ms.map)

            const draft = {
                id: uuid(),
                type: 'polygon',
                overlay,
                path: paths,
                feature: JSON.parse(JSON.stringify(feature)),
                geometryType: 'polygon',
                createdAt: new Date(),
                pathListeners: null,
            }

            ds.handleContextMenu(overlay)
            overlay.addListener('click', () => this.highlightRoiInList(draft.id))
            return draft
        },
        disposePolygonDraft(draft) {
            if (!draft) return

            const ms = useMaps()
            if (draft.pathListeners) {
                draft.pathListeners.forEach(listener => {
                    ms.google.maps.event.removeListener(listener)
                })
                draft.pathListeners = null
            }

            if (draft.overlay) {
                const rawOverlay = toRaw(draft.overlay)
                rawOverlay?.setMap?.(null)
            }
        },
        attachPolygonDraftListeners(draft, onCoordinatesChanged) {
            const ms = useMaps()
            if (!draft?.overlay || draft.type !== 'polygon') return

            if (draft.pathListeners) {
                draft.pathListeners.forEach(listener => {
                    ms.google.maps.event.removeListener(listener)
                })
            }

            const rawOverlay = toRaw(draft.overlay)
            const path = rawOverlay.getPath()

            const updateCoordinates = async () => {
                await this.syncPolygonDraftCoordinates(draft, onCoordinatesChanged)
                
            }

            draft.pathListeners = [
                ms.google.maps.event.addListener(path, 'set_at', updateCoordinates),
                ms.google.maps.event.addListener(path, 'insert_at', updateCoordinates),
                ms.google.maps.event.addListener(path, 'remove_at', updateCoordinates),
                ms.google.maps.event.addListener(rawOverlay, 'dragend', updateCoordinates),
            ]
        },
        async syncPolygonDraftCoordinates(draft, onCoordinatesChanged) {
            if (!draft?.overlay) return

            const rawOverlay = toRaw(draft.overlay)
            const newPath = rawOverlay.getPath()
            const pathArray = newPath.getArray()

            draft.path = pathArray.map(coord => ({
                lat: coord.lat(),
                lng: coord.lng(),
            }))

            const coordinates = pathArray.map(coord => [coord.lng(), coord.lat()])

            if (draft.feature?.geometry) {
                if (draft.type === 'polygon') {
                    if (coordinates.length > 0
                        && (coordinates[0][0] !== coordinates[coordinates.length - 1][0]
                            || coordinates[0][1] !== coordinates[coordinates.length - 1][1])) {
                        coordinates.push(coordinates[0])
                    }
                    draft.feature.geometry.coordinates = [coordinates]
                } else {
                    draft.feature.geometry.coordinates = coordinates
                }
            }

            if (typeof onCoordinatesChanged === 'function') {
                onCoordinatesChanged(draft)
            }
        },
        async updateElementCoordinates(elementId) {
            const ds = useDrawingStore()
            const element = this.geometryElements.find(el => el.id === elementId)

            if (!element || !element.overlay) return

            await this.syncPolygonDraftCoordinates(element, async () => {
                await ds.drawObstacle(elementId)
                setTimeout(() => {
                    this.isModified = false
                }, 100)

                if (element.isOld) {
                    element.isModified = true
                }
            })
            this.updateSegments(elementId)
        },
        async loadElementData() {
            const seg = useSegmentsStore()
            // seg.clearAll()
            const elementId = this.selectedElementId
            if (!elementId) return
            
            const element = this.geometryElements.find(el => el.id === elementId)
            if (element && element.properties) {
                this.ensureDefaultType(element)
                this.localData = {
                    type: element.properties.type || '',
                    message: element.properties.message || '',
                    height: element.properties.height || null,
                    validFrom: element.properties.validFrom ? new Date(element.properties.validFrom) : null,
                    validTo: element.properties.validTo ? new Date(element.properties.validTo) : null,
                    driveable: element.properties.driveable ?? false,
                    active: element.properties.active ?? true,
                    isSmall: element.properties.isSmall ?? false,
                    headlandsSegments: element.properties.headlandsSegments ?? null,
                    head_segments_id: element.properties.head_segments_id ?? null
                }
                this.originalData = JSON.parse(JSON.stringify(this.localData))
                if (this.localData.type === 'cutout' && this.localData.headlandsSegments && !this.localData.driveable) {
                    seg.showAllSegmentsLines({headland_segments: element.properties.headlandsSegments})
                    await this.checkSegmentation(this.localData.headlandsSegments)
                } else {
                    await this.drawSmallCutoutHeadlands(this.localData)
                }
            } else {
                // Reset to defaults for new element
                this.localData = {
                    type: '',
                    message: '',
                    height: null,
                    validFrom: null,
                    validTo: null,
                    driveable: false,
                    active: true,
                    isSmall: true,
                    headlands: null,
                    headlandsSegments: null,
                    head_segments_id: null,
                    segments: []
                }
                this.originalData = JSON.parse(JSON.stringify(this.localData))
            }
        },
        hasUnsavedChanges() {
            if (!this.originalData) return false
            
            // Check input changes
            const hasInputChanges = JSON.stringify(this.localData) !== JSON.stringify(this.originalData)
            
            // Check geo changes for old elements
            const element = this.geometryElements.find(el => el.id === this.selectedElementId)
            const hasGeoChanges = element?.isOld && element?.originalPath && JSON.stringify(element.path) !== JSON.stringify(element.originalPath)
            
            return hasInputChanges || hasGeoChanges
        },
        discardChanges() {
            const seg = useSegmentsStore()
            // Reload the original input data
            if (this.originalData) {
                this.localData = JSON.parse(JSON.stringify(this.originalData))
            }
            
            // Restore original geometry for old elements
            const element = this.geometryElements.find(el => el.id === this.selectedElementId)
            if (element?.isOld && element?.originalPath) {
                this.restoreOriginalGeometry(element)
            }
            seg.clearAll()
        },
        // Restore original geometry for old element (on discard changes)
        restoreOriginalGeometry(element) {
            if (!element.originalPath || !element.originalGeometry) return
            
            const fcs = useFieldsConnectionStore()
            const mapStore = useMaps()
            const ds = useDrawingStore()
            
            // Restore data
            element.path = JSON.parse(JSON.stringify(element.originalPath))
            element.feature.geometry = JSON.parse(JSON.stringify(element.originalGeometry))
            element.isModified = false
            
            // Update overlay on map
            const overlay = fcs.generatedROIs?.find(roi => toRaw(roi).roiId === element.id)
            if (overlay) {
                const raw = toRaw(overlay)
                if (element.type === 'point') {
                    raw.position = element.path[0]
                } else {
                    raw.setPath(element.path.map(p => new mapStore.google.maps.LatLng(p.lat, p.lng)))
                }
            }
            
            // Redraw SVG
            setTimeout(async () => { 
                await ds.drawObstacle(element.id)
                this.isModified = true
            }, 200)
        },
        applyLocalDataChanges() {
            const elementId = this.selectedElementId
            if (!elementId) return false
            
            const element = this.geometryElements.find(el => el.id === elementId)

            const seg = useSegmentsStore()

            let headlandSegmentsData = null
            if (seg.allSegmentsLinesCoords.length > 0) {
                // Each segment is a GeometryCollection that contains a MultiLineString
                headlandSegmentsData = {
                    type: 'FeatureCollection',
                    features: seg.allSegmentsLinesCoords.map((geomCollection, index) => {
                        return {
                            type: 'Feature',
                            properties: {
                                field_geom_type: 'headland_segments',
                                counter: index
                            },
                            geometry: geomCollection
                        }
                    })
                }
            }
            element.properties.headlandsSegments = headlandSegmentsData
            this.updateElementProperties(elementId, { ...this.localData })
            this.originalData = JSON.parse(JSON.stringify(this.localData))
            // Mark old element as modified
            if (element && element.isOld) element.isModified = true
            seg.clearAll()
            return true
        },
        clearSelection() {
            const seg = useSegmentsStore()
            // Disable editing on old element when closing
            if (this.selectedElementId) {
                const element = this.geometryElements.find(el => el.id === this.selectedElementId)
                if (element && element.isOld) {
                    this.setOldElementEditable(element, false)
                }
            }
            seg.clearAll()
            
            this.localData = {
                type: '',
                message: '',
                height: null,
                validFrom: null,
                validTo: null,
                driveable: false,
                active: true,
                isSmall: true,
                headlandsSegments: null,
                head_segments_id: null,
            }
            this.originalData = null
            this.selectedElementId = null
        },
        processPolygonFeature(feature) {
            const element = this.createEditablePolygonDraft(feature)
            if (!element) return null

            element.roisType = this.roisType
            element.properties = this.createDefaultProperties(element.geometryType || 'polygon')

            this.geometryElements.push(element)
            this.attachPolygonDraftListeners(element, async () => {
                this.isModified = true
                await this.updateElementCoordinates(element.id)
            })

            return element
        },
        processLineStringGeometry(geometry) {
            const mapStore = useMaps()

            const path = geometry.coordinates.map(([lng, lat]) => ({
                lat,
                lng
            }))

            const overlay = new mapStore.google.maps.Polyline({
                path,
                strokeWeight: 2,
                strokeColor: '#ff0000',
                clickable: true,
                editable: true,
                zIndex: 1000,
            })
            overlay.setMap(mapStore.map)

            const elementId = uuid()
            overlay.addListener('click', () => this.highlightRoiInList(elementId))

            const element = {
                id: elementId,
                type: 'line',
                overlay,
                path,
                feature: {
                    type: 'Feature',
                    geometry,
                    properties: {
                        field_geom_type: 'LineString',
                    },
                },
                geometryType: 'linestring',
                roisType: this.roisType,
                createdAt: new Date(),
                properties: this.createDefaultProperties('linestring')
            }

            return element
        },
        async uploadGeometryFile(file) {
            const formData = new FormData()
            formData.append('file', file)

            try {
                const response = await postExtractGeometryFromShape(formData)

                // Handle not supported file format
                if (response.status === 422) {
                    return { success: false, error: 'Unsupported file format' }
                }
                
                if (!response) {
                    throw new Error('Failed to upload file!')
                }

                // Process Polygon features from cutout response
                if (response.cutout && response.cutout.features) {
                    for (const feature of response.cutout.features) {
                        if (feature.properties && feature.properties.field_geom_type === 'cutout') {
                            this.processPolygonFeature(feature)
                        }
                    }
                }
                // Process LineString response
                else if (response.line && response.line.type === 'LineString') {
                    const line = this.processLineStringGeometry(response.line)
                    if (line) {
                        const ds = useDrawingStore()

                        this.setupGeoListeners(line.id)
                        ds.handleContextMenu(line.overlay)
                        this.geometryElements.push(line)
                        this.addPathEditListeners(line.id)
                    }
                }

                // Set TerraDraw to select mode if it's enabled
                const mapStore = useMaps()
                if (mapStore.terraDraw && mapStore.terraDraw.enabled) {
                    mapStore.terraDraw.setMode('select')
                }

                // Center map on current field + uploaded geometry
                this.centerMapOnFieldAndGeometry()

                return { success: true }
            } catch (error) {
                console.error('There was a problem with the fetch operation:', error)
                return { success: false, error: error.message }
            }
        },
        centerMapOnFieldAndGeometry() {
            const mapStore = useMaps()
            const fcs = useFieldsConnectionStore()
            const bounds = new mapStore.google.maps.LatLngBounds()
        
            // Add field bounds if exists
            if (fcs.startField_id) {
                const field = mapStore.fields.find(f => f.id === fcs.startField_id)
                if (field?.bounds) {
                    bounds.extend(field.bounds.getNorthEast())
                    bounds.extend(field.bounds.getSouthWest())
                }
            }
        
            // Add all geometry elements bounds
            this.geometryElements.forEach(element => {
                if (!element.path) return
        
                element.path.forEach(point => {
                    if (point?.lat != null && point?.lng != null) {
                        bounds.extend(point)
                    }
                })
            })
        
            // Center map if we have bounds
            if (!bounds.isEmpty()) {
                mapStore.map.fitBounds(bounds, { padding: 50 })
            }
        },
        async deleteOldROI(id) {
            const fcs = useFieldsConnectionStore()
            if (!fcs.startField_id) {
                console.error('Start field not found')
                return false
            }

            if (!id) {
                console.error('ROI ID not found')
                return false
            }

            try {
                await deleteOldFieldROI(fcs.startField_id, id)

                const overlay = fcs.generatedROIs?.find(roi => toRaw(roi).roiId === id)
                if (overlay) {
                    toRaw(overlay).setMap?.(null)
                }
                
                // Remove from UI list
                this.geometryElements = this.geometryElements.filter(el => el.id !== id)
                
                return true
            } catch (error) {
                console.error('Error deleting ROI:', error)
                return false
            }
        },
        async drawSmallCutoutHeadlands(roiData, givenElementID = null, isGonnaShow = true) {
            const dS = useDrawingStore()
            const fcs = useFieldsConnectionStore()
            const seg = useSegmentsStore()
            if (isGonnaShow || roiData.type !== 'cutout') {
                seg.clearAll()
            }
            const noSelectedElementID = this.selectedElementId === null || this.selectedElementId === undefined
            if (givenElementID === null && noSelectedElementID) {
                console.warn("No selected element id for headland generation")
                return
            }
            const elementIdToCheck = givenElementID || this.selectedElementId
            const element_geom = this.geometryElements.find(element => element.id === elementIdToCheck)
            if (element_geom.type === 'point') {
                return
            }
            
            if (roiData.type !== 'cutout' || !roiData || !element_geom.feature.geometry || roiData.driveable ) {
                seg.clearAll()
                return
            }
            roiData.geometry = element_geom.feature.geometry

            const data = {
                type: roiData.type,
                roi: {
                    type: "FeatureCollection",
                    features: [
                        {
                            type: "Feature",
                            geometry: roiData.geometry,
                            properties: {
                                field_geom_type: "roi"  // matches fgt.ROIS constant
                            }
                        }
                    ]
                },
                headland_config: null,
                headlands: null,
                field_id: fcs.startField_id,
                offset: 0.4,
                trackwidth: dS.trackwidth,
                turning_radius: null,
                border: null,
                angle_diff_lower_threshold: dS.minAngle,
                angle_diff_upper_threshold: dS.maxAngle
            }
            const result = await generateROIHeadlands(data)

            if (!result || !result.roi || !result.roi_segments) {
                console.error('Invalid response from generateROIHeadlands:', result)

                let cause
                const isNetworkError = ['NETWORK_ERROR', 'CONNECTION_ERROR', 'TIMEOUT_ERROR'].includes(result?.problem)

                if (isNetworkError) {
                    cause = 'The server is unreachable. Please check your internet connection and try again.'
                } else if (result?.data?.reason) {
                    // API returned a structured error — prefer the server message when available
                    cause = result.data.info || 'Malformed geometry data. Please check the geometry and try again.'
                } else {
                    // 200 without roi/roi_segments, 500 with empty body, etc.
                    cause = 'Unexpected error. Please try again.'
                }

                this.toastMessage = ['error', 'Failed to generate ROI headland segments.', cause]
                return
            }


            // look how its done for the headland segments
            const headlands = result.roi;  // FeatureCollection
            const headland_segments = result.roi_segments;  // FeatureCollection

            roiData.headlands = headlands
            roiData.headland_segments = headland_segments
            
            roiData.headlandsSegments = headland_segments
            
            // Also update the actual element in geometryElements
            if (element_geom) {
                element_geom.headland_segments = headland_segments
                element_geom.properties.headlandsSegments = headland_segments
            }
            if (isGonnaShow) {
                seg.showAllSegmentsLines({headland_segments: result.roi_segments})
                this.checkSegmentation(roiData.headland_segments)
            }
        },
        async checkSegmentation(headland_segments) {
            const ds = useDrawingStore()
            const seg = useSegmentsStore()
            const data = {
                headland_segments: headland_segments,
                angle_diff_lower_threshold: ds.minAngle,
                angle_diff_upper_threshold: ds.maxAngle
            }
            const response = await postSegmentationInteractionPoints(data)
            if (response === false) throw new Error('No response from segmentation interaction points')

            const features = Array.isArray(response) ? response : response.features ?? []
            seg.interactionPointFeatures = features
            seg.showSegmentsPoints = true
            seg.applySegmentsPointsVisibility()
        },
        updateSegments(geomElementId) {
            let dataToUpdate = null
            let shouldDrawSegments = true
            if (geomElementId !== this.selectedElementId) {
                dataToUpdate = this.geometryElements.find(el => el.id === geomElementId).properties
                shouldDrawSegments = false
            } else {
                dataToUpdate = this.localData
            }

            this.drawSmallCutoutHeadlands(dataToUpdate, geomElementId, shouldDrawSegments)
        },
    }
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useRoiStore, import.meta.hot))
}
